import React from "react"
import {
  Box,
  Typography,
  UnorderedList,
  UnorderedListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  DownIcon
} from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"

import { Reimbursement } from "#/types/reimbursement"
import { ExpenseState } from "#/utils/ExpenseState"

// Sole frontend trigger for the next-benefit-year "Pending" variant. The backend sets
// PENDING_NEXT_PLAN_YEAR; the broader SCHEDULED+future-start case isn't frontend-computable.
export const isScheduledForNextBenefitYear = (transaction: Reimbursement): boolean =>
  transaction.expenseState === ExpenseState.PENDING_NEXT_PLAN_YEAR

interface DirectPayAmountBreakdownProps {
  amount: number
  eligibleAmount: number
  amountAppliedToDeductible?: number | null
  amountAppliedToCostShare?: number | null
  formatCurrency: (value: number) => string
}

// Renders "Carrot pays / You pay / (deductible, cost share, amount-over) bullets".
// Shared by the live amounts block and the Original-amounts accordion.
const DirectPayAmountBreakdown = ({
  amount,
  eligibleAmount,
  amountAppliedToDeductible,
  amountAppliedToCostShare,
  formatCurrency
}: DirectPayAmountBreakdownProps) => {
  const intl = useIntl()

  const amountAppliedToDeductibleString = amountAppliedToDeductible ? formatCurrency(amountAppliedToDeductible) : null
  const amountAppliedToCostShareString = amountAppliedToCostShare ? formatCurrency(amountAppliedToCostShare) : null
  const amountOverAvailableFunds =
    eligibleAmount - amount - (amountAppliedToDeductible ?? 0) - (amountAppliedToCostShare ?? 0)
  const amountOverAvailableFundsString = amountOverAvailableFunds > 0 ? formatCurrency(amountOverAvailableFunds) : null

  return (
    <>
      <Typography variant="body1" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.xxs })}>
        <FormattedMessage defaultMessage="Carrot pays: {amount}" values={{ amount: formatCurrency(amount) }} />
      </Typography>
      <Typography variant="body1" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.xxs })}>
        <FormattedMessage
          defaultMessage="You pay: {memberAmount}"
          values={{ memberAmount: formatCurrency(eligibleAmount - amount) }}
        />
      </Typography>
      <Box sx={(theme) => ({ paddingInlineStart: theme.tokens.spacing.sm })}>
        <UnorderedList disablePadding={true}>
          {amountAppliedToDeductibleString && (
            <UnorderedListItem disablePadding={true}>
              <ListItemText
                primary={intl.formatMessage(
                  { defaultMessage: "Applied to deductible: {amountAppliedToDeductibleString}" },
                  { amountAppliedToDeductibleString }
                )}
              />
            </UnorderedListItem>
          )}
          {amountAppliedToCostShareString && (
            <UnorderedListItem disablePadding={true}>
              <ListItemText
                primary={intl.formatMessage(
                  { defaultMessage: "Applied to cost share: {amountAppliedToCostShareString}" },
                  { amountAppliedToCostShareString }
                )}
              />
            </UnorderedListItem>
          )}
          {amountOverAvailableFundsString && (
            <UnorderedListItem disablePadding={true}>
              <ListItemText
                primary={intl.formatMessage(
                  { defaultMessage: "Amount over available funds: {amountOverAvailableFundsString}" },
                  { amountOverAvailableFundsString }
                )}
              />
            </UnorderedListItem>
          )}
        </UnorderedList>
      </Box>
    </>
  )
}

// Pending cost lines for the next-benefit-year variant: "Carrot pays / You pay" rendered as a
// localized "Pending" token (reusing the existing line message IDs), no numeric bullets.
const DirectPayPendingBreakdown = () => {
  const intl = useIntl()
  const pending = intl.formatMessage({ defaultMessage: "Pending" })
  return (
    <>
      <Typography variant="body1" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.xxs })}>
        <FormattedMessage defaultMessage="Carrot pays: {amount}" values={{ amount: pending }} />
      </Typography>
      <Typography variant="body1" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.xxs })}>
        <FormattedMessage defaultMessage="You pay: {memberAmount}" values={{ memberAmount: pending }} />
      </Typography>
    </>
  )
}

interface DirectPayAmountsProps {
  transaction: Reimbursement
  formatCurrency: (value: number) => string
}

type DirectPayAmountsVariant = "pendingNextYear" | "adjusted" | "standard"

export const DirectPayAmounts = ({ transaction, formatCurrency }: DirectPayAmountsProps) => {
  // DirectPayAmounts only renders for DirectPay expenses (shouldShowFullContent === isDirectPayExpense), so the parent's isDirectPayExpense guard is implicit here.
  const intl = useIntl()

  const hasAdjustment =
    !!transaction.adjustmentDatetime && transaction.originalAmount != null && transaction.originalEligibleAmount != null

  // A next-plan-year expense is never "adjusted" (no adjustmentDatetime yet), so pendingNextYear takes precedence.
  const variant: DirectPayAmountsVariant = isScheduledForNextBenefitYear(transaction)
    ? "pendingNextYear"
    : hasAdjustment
    ? "adjusted"
    : "standard"

  if (variant === "pendingNextYear") {
    const formattedNextPlanYearStart = transaction.nextPlanYearStartDate
      ? intl.formatDate(new Date(transaction.nextPlanYearStartDate), {
          year: "numeric",
          month: "long",
          day: "numeric",
          timeZone: "UTC"
        })
      : null

    return (
      <Box>
        <Typography
          variant="body1"
          fontWeight="bold"
          color="secondary"
          sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.xxs })}
        >
          <FormattedMessage defaultMessage="Amounts" />
        </Typography>
        <Typography variant="body1" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.xxs })}>
          <FormattedMessage
            defaultMessage="Total: {eligibleAmount}"
            values={{ eligibleAmount: formatCurrency(transaction.eligibleAmount) }}
          />
        </Typography>
        <DirectPayPendingBreakdown />
        <Typography variant="body1" sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.sm })}>
          <FormattedMessage defaultMessage="This service is expected to start in the next benefit year. We'll review it when we have your updated coverage details and deductible information (if required)." />
        </Typography>
        <Typography variant="body1" sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.sm })}>
          <FormattedMessage defaultMessage="If the service is approved, we'll schedule it automatically and deduct Carrot's responsibility from your available funds." />
          {/* Date sentence renders only when the backend supplied nextPlanYearStartDate. In normal
              operation it's always present for a PENDING_NEXT_PLAN_YEAR expense (the company has a
              plan); omitting it guards the rare no-active-plan edge without inventing copy. */}
          {formattedNextPlanYearStart && (
            <>
              {" "}
              <FormattedMessage
                defaultMessage="You'll receive a status update sometime after {date}."
                values={{ date: formattedNextPlanYearStart }}
              />
            </>
          )}
        </Typography>
      </Box>
    )
  }

  // Below this point only "adjusted" and "standard" remain; they branch on hasAdjustment directly (variant isn't re-checked here).

  // Refund vs owe-more
  const memberNewPays = hasAdjustment ? transaction.eligibleAmount - transaction.amount : 0
  const memberOriginalPays = hasAdjustment ? transaction.originalEligibleAmount - transaction.originalAmount : 0
  const adjustmentDifference = memberNewPays - memberOriginalPays
  const memberGetsRefund = adjustmentDifference < 0
  const memberOwesMore = adjustmentDifference > 0

  return (
    <>
      <Box>
        {hasAdjustment ? (
          <>
            <Typography
              variant="body1"
              fontWeight="bold"
              color="secondary"
              sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.xxs })}
            >
              <FormattedMessage defaultMessage="Updated amounts" />
            </Typography>
            <Typography variant="body1" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.xxs })}>
              <FormattedMessage
                defaultMessage="Total: {eligibleAmount}"
                values={{ eligibleAmount: formatCurrency(transaction.eligibleAmount) }}
              />
            </Typography>
          </>
        ) : (
          <Typography
            variant="body1"
            fontWeight="bold"
            color="secondary"
            sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.xxs })}
          >
            <FormattedMessage
              defaultMessage="Amount: {eligibleAmount}"
              values={{ eligibleAmount: formatCurrency(transaction.eligibleAmount) }}
            />
          </Typography>
        )}
        <DirectPayAmountBreakdown
          amount={transaction.amount}
          eligibleAmount={transaction.eligibleAmount}
          amountAppliedToDeductible={transaction.amountAppliedToDeductible}
          amountAppliedToCostShare={transaction.amountAppliedToCostShare}
          formatCurrency={formatCurrency}
        />

        {/* Adjustment explanation text */}
        {hasAdjustment && memberGetsRefund && (
          <Typography
            variant="body1"
            fontStyle="italic"
            sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.sm })}
          >
            <FormattedMessage
              defaultMessage="You can expect a refund of {refundAmount} from your provider. Please check your email for more information."
              values={{ refundAmount: formatCurrency(Math.abs(adjustmentDifference)) }}
            />
          </Typography>
        )}
        {hasAdjustment && memberOwesMore && (
          <Typography
            variant="body1"
            fontStyle="italic"
            sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.sm })}
          >
            <FormattedMessage
              defaultMessage="Your provider will collect an additional payment of {additionalAmount} from you. Please check your email for more information."
              values={{ additionalAmount: formatCurrency(adjustmentDifference) }}
            />
          </Typography>
        )}
      </Box>

      {/* Original amounts (shown only for adjusted transactions) */}
      {hasAdjustment && (
        <Accordion defaultExpanded sx={{ "&::before": { display: "none" } }}>
          <AccordionSummary
            expandIcon={<DownIcon />}
            aria-controls="original-amounts-content"
            id="original-amounts-header"
          >
            <Typography variant="body1" fontWeight="bold" color="secondary">
              <FormattedMessage defaultMessage="Original amounts" />
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Typography variant="body1" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.xxs })}>
                <FormattedMessage
                  defaultMessage="Total: {eligibleAmount}"
                  values={{ eligibleAmount: formatCurrency(transaction.originalEligibleAmount) }}
                />
              </Typography>
              <DirectPayAmountBreakdown
                amount={transaction.originalAmount}
                eligibleAmount={transaction.originalEligibleAmount}
                amountAppliedToDeductible={transaction.originalAmountAppliedToDeductible}
                amountAppliedToCostShare={transaction.originalAmountAppliedToCostShare}
                formatCurrency={formatCurrency}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  )
}
